import React, { useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { RootState } from "../../../app/store";
import { set } from "../../../app/globalSlice";
import { useTranslation } from "react-i18next";
import { requestApi } from "../../../helpers/RequestApi";
import { DELETE, GET, PATCH, POST } from "../../../utils/MethodUtils";
import { INVOICE_TEMPLATE_URL } from "../../../utils/UrlsUtils";
import { toastr } from "react-redux-toastr";
import {
  Box,
  Container,
  DialogProps,
  Select,
  TextareaAutosize,
} from "@mui/material";
import LoginComponent from "../../../components/common/user/LoginComponent";
import { InvoiceTemplateInterface } from "../../../interfaces/EdiInterface";
import Paper from "@mui/material/Paper";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import { styled, useTheme } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import SaveIcon from "@mui/icons-material/Save";
import { LoadingButton } from "@mui/lab";
import { InputInterface } from "../../../interfaces/InputInterface";
import getErrorApi from "../../../helpers/GetErrorApi";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import useMediaQuery from "@mui/material/useMediaQuery";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}, &.${tableCellClasses.footer}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

interface FormState {
  systemPrompt: InputInterface;
}

interface State {
  invoiceTemplate: InvoiceTemplateInterface;
  setInvoiceTemplates: Function;
}

const InvoiceTemplate: React.FC<State> = React.memo(
  ({ invoiceTemplate, setInvoiceTemplates }) => {
    const [loading, setLoading] = React.useState<boolean>(false);
    const [openDialog, setOpenDialog] = React.useState<boolean>(false);
    const [maxWidth] = React.useState<DialogProps["maxWidth"]>("lg");
    const handleClose = React.useCallback(() => {
      setOpenDialog(false);
    }, []);
    const handleOpen = React.useCallback(() => {
      setOpenDialog(true);
    }, []);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
    const { t } = useTranslation();
    const token = useAppSelector((state: RootState) => state.globalState.token);
    const fournisseurs = useAppSelector(
      (state: RootState) => state.globalState.fournisseurs
    );
    const dispatch = useAppDispatch();
    const suppliers = useAppSelector(
      (state: RootState) => state.globalState.suppliers
    );
    const getSuppliers = React.useCallback(() => {
      return (
        fournisseurs?.filter((f) => suppliers?.includes(f.userIdentifier)) ?? []
      ).sort((a, b) => a.userIdentifier.localeCompare(b.userIdentifier));
    }, [fournisseurs, suppliers]);
    const [thisSuppliers, setThisSuppliers] = React.useState(getSuppliers());
    const [values, setValues] = React.useState<FormState>({
      systemPrompt: { value: invoiceTemplate.systemPrompt, error: "" },
    });

    const deleteInvoiceTemplate = React.useCallback(async () => {
      if (!invoiceTemplate.id) {
        setInvoiceTemplates((x: InvoiceTemplateInterface[]) => {
          if (!x) {
            return x;
          }
          x = x.filter(
            (y) => y.userIdentifier !== invoiceTemplate.userIdentifier
          );
          return [...x];
        });
      } else {
        handleOpen();
      }
    }, [
      dispatch,
      handleOpen,
      invoiceTemplate.id,
      invoiceTemplate.userIdentifier,
      setInvoiceTemplates,
    ]);

    const confirmDelete = React.useCallback(async () => {
      setLoading(true);
      const response = await requestApi({
        method: DELETE,
        path: INVOICE_TEMPLATE_URL + "/" + invoiceTemplate.id,
        allowError: true,
        token: token,
      });
      if (response.statusCode === 204) {
        setInvoiceTemplates((x: InvoiceTemplateInterface[]) => {
          if (!x) {
            return x;
          }
          x = x.filter(
            (y) => y.userIdentifier !== invoiceTemplate.userIdentifier
          );
          dispatch(set({ invoiceTemplates: [...x] }));
          return [...x];
        });
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        for (let message of getErrorApi(response.content)) {
          toastr.error(t("word.error"), t(message));
        }
      }
      setLoading(false);
    }, [
      dispatch,
      invoiceTemplate.id,
      invoiceTemplate.userIdentifier,
      setInvoiceTemplates,
      t,
      token,
    ]);

    const saveInvoiceTemplate = React.useCallback(async () => {
      setLoading(true);
      let url = INVOICE_TEMPLATE_URL;
      let method = POST;
      if (invoiceTemplate.id) {
        url += "/" + invoiceTemplate.id;
        method = PATCH;
      }
      const response = await requestApi({
        method: method,
        path: url,
        allowError: true,
        token: token,
        body: {
          userIdentifier: invoiceTemplate.userIdentifier,
          systemPrompt: values.systemPrompt.value,
        },
      });
      if (response.statusCode === 200 || response.statusCode === 201) {
        setInvoiceTemplates((x: InvoiceTemplateInterface[]) => {
          const r = [...x];
          const index = r.findIndex(
            (y) => y.userIdentifier === invoiceTemplate.userIdentifier
          );
          r[index] = response.content;
          dispatch(set({ invoiceTemplates: r }));
          return r;
        });
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        for (let message of getErrorApi(response.content)) {
          toastr.error(t("word.error"), t(message));
        }
      }
      setLoading(false);
    }, [
      dispatch,
      invoiceTemplate.id,
      invoiceTemplate.userIdentifier,
      setInvoiceTemplates,
      t,
      token,
      values.systemPrompt.value,
    ]);

    const handleChange = React.useCallback(
      (prop: keyof FormState) =>
        (event: React.ChangeEvent<HTMLTextAreaElement>) => {
          setValues((v) => {
            return {
              ...v,
              [prop]: { ...v[prop], value: event.target.value, error: "" },
            };
          });
        },
      []
    );

    React.useEffect(() => {
      setThisSuppliers(getSuppliers());
    }, [fournisseurs, suppliers, invoiceTemplate.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const supplier = thisSuppliers.find(
      (x) => x.userIdentifier === invoiceTemplate.userIdentifier
    );

    return (
      <>
        <Dialog
          maxWidth={maxWidth}
          fullScreen={fullScreen}
          onClose={handleClose}
          open={openDialog}
        >
          <DialogContent>{t("sentence.deleteInvoiceTemplate")}</DialogContent>
          <DialogActions sx={{ justifyContent: "space-between" }}>
            <LoadingButton disabled={loading} onClick={handleClose}>
              {t("word.no")}
            </LoadingButton>
            <LoadingButton
              variant="contained"
              loading={loading}
              onClick={confirmDelete}
            >
              {t("word.yes")}
            </LoadingButton>
          </DialogActions>
        </Dialog>
        <StyledTableRow
          key={
            (invoiceTemplate.id?.toString() ?? "") +
            (invoiceTemplate.userIdentifier?.toString() ?? "")
          }
        >
          <StyledTableCell>
            {invoiceTemplate.userIdentifier ? (
              <>
                [{supplier?.userIdentifier}] {supplier?.ctIntitule}
              </>
            ) : (
              t("sentence.byDefault")
            )}
          </StyledTableCell>
          <StyledTableCell sx={{ minWidth: 400 }}>
            <TextareaAutosize
              maxRows={4}
              placeholder="Maximum 4 rows"
              value={values.systemPrompt.value}
              onChange={handleChange("systemPrompt")}
              style={{
                width: "100%",
                resize: "vertical",
              }}
            />
          </StyledTableCell>
          <StyledTableCell>
            {invoiceTemplate.userIdentifier && (
              <>
                <IconButton onClick={deleteInvoiceTemplate}>
                  <DeleteIcon />
                </IconButton>
                {(invoiceTemplate.id === null ||
                  values.systemPrompt.value !==
                    invoiceTemplate.systemPrompt) && (
                  <LoadingButton
                    variant="text"
                    color="inherit"
                    sx={{
                      borderRadius: "50%",
                      minWidth: "auto",
                      padding: "8px",
                      color: "rgba(0, 0, 0, 0.54)",
                    }}
                    loading={loading}
                    onClick={saveInvoiceTemplate}
                  >
                    <SaveIcon />
                  </LoadingButton>
                )}
              </>
            )}
          </StyledTableCell>
        </StyledTableRow>
      </>
    );
  }
);

const OpenAiComponent: React.FC = React.memo(() => {
  const refreshPage = useAppSelector(
    (state: RootState) => state.globalState.refreshPage
  );
  const componentRef = useRef();
  const dispatch = useAppDispatch();
  const [init, setInit] = React.useState(false);
  const [invoiceTemplates, setInvoiceTemplates] = React.useState<
    InvoiceTemplateInterface[] | undefined
  >(undefined);
  const { t } = useTranslation();
  const token = useAppSelector((state: RootState) => state.globalState.token);
  const user = useAppSelector((state: RootState) => state.globalState.user);
  const fournisseurs = useAppSelector(
    (state: RootState) => state.globalState.fournisseurs
  );
  const suppliers = useAppSelector(
    (state: RootState) => state.globalState.suppliers
  );
  const getSuppliers = React.useCallback(() => {
    return (
      fournisseurs?.filter((f) => suppliers?.includes(f.userIdentifier)) ?? []
    ).sort((a, b) => a.userIdentifier.localeCompare(b.userIdentifier));
  }, [fournisseurs, suppliers]);
  const [thisSuppliers, setThisSuppliers] = React.useState(getSuppliers());
  const load = React.useCallback(
    async (force: boolean = false) => {
      setInit(true);
      if (!user || (invoiceTemplates !== undefined && !force)) {
        dispatch(set({ refreshPage: false }));
        return;
      }
      setInvoiceTemplates(undefined);
      const response = await requestApi({
        method: GET,
        path: INVOICE_TEMPLATE_URL,
        allowError: false,
        token: token,
        paginate: true,
      });
      if (response.statusCode === 200) {
        setInvoiceTemplates(response.content["hydra:member"]);
        dispatch(set({ invoiceTemplates: response.content["hydra:member"] }));
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        toastr.error(t("word.error"), t("error.tryAgain"));
      }
      dispatch(set({ refreshPage: false }));
    },
    [dispatch, invoiceTemplates, t, token, user]
  );

  const changeUserIdentifier = React.useCallback(
    async (userIdentifier: string) => {
      setInvoiceTemplates((x) => {
        if (!x) {
          return x;
        }
        const newInvoiceTemplate: InvoiceTemplateInterface = {
          id: null,
          userIdentifier: userIdentifier,
          systemPrompt:
            x.find((y) => y.userIdentifier === null)?.systemPrompt ?? "",
          created: "",
        };
        return [...x, newInvoiceTemplate];
      });
    },
    []
  );

  React.useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (init && refreshPage) {
      load(true);
    }
  }, [init, refreshPage]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    setThisSuppliers(getSuppliers());
  }, [fournisseurs, suppliers, invoiceTemplates]); // eslint-disable-line react-hooks/exhaustive-deps

  const alreadyCreated = invoiceTemplates?.map((x) => x.userIdentifier) ?? [];
  return (
    <>
      <LoginComponent redirect={null} requireAdmin={true}>
        <Box ref={componentRef}>
          <Container sx={{ marginY: 2 }}>
            <TableContainer component={Paper}>
              <Table size="small" sx={{ tableLayout: "auto", width: "100%" }}>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>{t("word.supplier")}</StyledTableCell>
                    <StyledTableCell sx={{ minWidth: 400 }}>
                      {t("word.text")}
                    </StyledTableCell>
                    <StyledTableCell></StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoiceTemplates?.map((invoiceTemplate) => {
                    return (
                      <InvoiceTemplate
                        invoiceTemplate={invoiceTemplate}
                        setInvoiceTemplates={setInvoiceTemplates}
                        key={invoiceTemplate.userIdentifier ?? "null"}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {invoiceTemplates && (
              <Box>
                <FormControl sx={{ marginTop: 2 }}>
                  <InputLabel>{t("sentence.addSupplier")}</InputLabel>
                  <Select
                    value={""}
                    sx={{
                      minWidth: 320,
                    }}
                    label={t("sentence.addSupplier")}
                    onChange={(e) => {
                      changeUserIdentifier(e.target.value);
                    }}
                  >
                    {thisSuppliers.map((s) => (
                      <MenuItem
                        disabled={alreadyCreated.includes(s.userIdentifier)}
                        value={s.userIdentifier}
                        key={s.userIdentifier}
                      >
                        [{s.userIdentifier}] {s.ctIntitule}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Container>
        </Box>
      </LoginComponent>
    </>
  );
});

export default OpenAiComponent;
